import React, { useEffect, useState, useRef } from "react";
import { Button, Form, Tabs, TabsProps, notification, Popconfirm } from "antd";
import { useParams } from "react-router-dom";
import { TbEditCircle } from "react-icons/tb";
import JoditEditor from "jodit-react";

import {
  editLongDescriptionBettingWebsite,
  getBettingWebsiteByID,
} from "../action";

type Props = {
  fetchData: () => void;
};

interface FormValues {
  long: {
    en: string;
    ar: string;
    fr: string;
  };
}

const EditLongDescription: React.FC<Props> = ({ fetchData }) => {
  const [form] = Form.useForm<FormValues>();
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();

  const fetchItem = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const website = await getBettingWebsiteByID(id);
      if (website) {
        form.setFieldsValue({
          long: {
            en: website?.MainInfo?.Description?.Long?.En || "",
            ar: website?.MainInfo?.Description?.Long?.Ar || "",
            fr: website?.MainInfo?.Description?.Long?.Fr || "",
          },
        });
      }
    } catch (err) {
      api.error({ message: "Failed to fetch website" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsLoading(true);
      const success = await editLongDescriptionBettingWebsite(id!, values.long);
      if (success) {
        fetchData();
        api.success({ message: "Long description updated successfully" });
      }
    } catch (err: any) {
      api.error({
        message: "Failed to update long description",
        description:
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          "Unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItem();
  }, [id]);

  const languages = ["en", "ar", "fr"] as const;

  const tabItems: TabsProps["items"] = languages.map((lang) => ({
    key: lang,
    label: lang.toUpperCase(),
    children: (
      <Form.Item name={["long", lang]} label={`Long Description (${lang})`}>
        <JoditEditor
          ref={useRef(null)}
          value={form.getFieldValue(["long", lang])}
          onBlur={(newContent) => {
            form.setFieldValue(["long", lang], newContent);
          }}
          config={{
            readonly: false,
            direction: lang === "ar" ? "rtl" : "ltr",
            placeholder: `Write long description in ${lang.toUpperCase()}...`,
            height: "50vh",
          }}
        />
      </Form.Item>
    ),
  }));

  return (
    <Form form={form} layout="vertical">
      {contextHolder}
      <Tabs defaultActiveKey="en" items={tabItems} />

      <Form.Item shouldUpdate>
        {() => (
          <Popconfirm
            title="Are you sure you want to edit the long description?"
            onConfirm={handleSubmit}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<TbEditCircle />}
              loading={isLoading}
              type="primary"
              className="w-full"
            >
              {isLoading ? "Saving..." : "Edit Long Description"}
            </Button>
          </Popconfirm>
        )}
      </Form.Item>
    </Form>
  );
};

export default EditLongDescription;
